import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Linking, TouchableOpacity, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { useRouter } from 'expo-router';
import { useLanguage } from '../../contexts/LanguageContext';

const APP_VERSION =
  Constants.expoConfig?.version ||
  ((Constants as { manifest?: { version?: string } }).manifest?.version ?? 'unknown');
const APP_COMMIT = process.env.EXPO_PUBLIC_GIT_COMMIT_HASH?.trim().slice(0, 7);

export default function ContactScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const [tapCount, setTapCount] = useState(0);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);

  const handleEmailPress = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const handleUrlPress = (url: string) => {
    Linking.openURL(url);
  };

  const handleLogoPress = () => {
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    if (newTapCount >= 7) {
      setTapCount(0);
      return;
    }

    tapTimeoutRef.current = setTimeout(() => {
      setTapCount(0);
    }, 2000);
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('contact.title')}</Text>
      </View>

      <TouchableOpacity onPress={handleLogoPress} activeOpacity={0.7} style={styles.logoContainer}>
        <Image
          source={require('../../assets/images/redmed-logo.png')}
          style={styles.logo}
          resizeMode="contain"
          accessible={true}
          accessibilityLabel="RedMed logo"
        />
        {tapCount > 0 && tapCount < 7 ? (
          <View style={styles.tapIndicator}>
            <Text style={styles.tapIndicatorText}>{tapCount}/7</Text>
          </View>
        ) : null}
      </TouchableOpacity>

      <View style={styles.infoCard}>
        <Text style={styles.sectionTitle}>{t('contact.about')}</Text>
        <Text style={styles.description}>{t('contact.aboutDescription')}</Text>
      </View>

      <View style={styles.infoCard}>
        <Text style={styles.sectionTitle}>{t('contact.contactInfo')}</Text>

        <View style={styles.contactItem}>
          <Ionicons name="business-outline" size={20} color="#0066CC" />
          <Text style={styles.contactLabel}>{t('contact.company')}</Text>
          <Text style={styles.contactValue}>{t('contact.companyName')}</Text>
        </View>

        <TouchableOpacity
          style={styles.contactItem}
          onPress={() => handleEmailPress('m.wronski@bieszczady.gopr.pl')}
        >
          <Ionicons name="mail-outline" size={20} color="#0066CC" />
          <Text style={styles.contactLabel}>{t('contact.generalContact')}</Text>
          <Text style={[styles.contactValue, styles.linkText]}>
            m.wronski@bieszczady.gopr.pl
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.contactItem}
          onPress={() => handleEmailPress('admin@redmed.dev')}
        >
          <Ionicons name="person-outline" size={20} color="#0066CC" />
          <Text style={styles.contactLabel}>{t('contact.technicalContact')}</Text>
          <Text style={[styles.contactValue, styles.linkText]}>admin@redmed.dev</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.contactItem}
          onPress={() => handleUrlPress('https://redmed.dev/gopr/')}
        >
          <Ionicons name="link-outline" size={20} color="#0066CC" />
          <Text style={styles.contactLabel}>{t('contact.otherProjects')}</Text>
          <Text style={[styles.contactValue, styles.linkText]}>https://redmed.dev/gopr/</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.infoCard}>
        <Text style={styles.sectionTitle}>{t('contact.applicationInfo')}</Text>
        <Text selectable style={styles.description}>
          {t('contact.versionInfo', APP_VERSION)}{APP_COMMIT ? ` · ${APP_COMMIT}` : ''}
        </Text>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('contact.changelog')} style={styles.changelogButton}
          onPress={() => router.push('/(app)/changelog')}>
          <Ionicons name="time-outline" size={20} color="#0066CC" />
          <Text style={styles.changelogLabel}>{t('contact.changelog')}</Text>
          <Ionicons name="chevron-forward" size={18} color="#0066CC" />
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  changelogButton: { minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 },
  changelogLabel: { flex: 1, fontSize: 14, color: '#0066CC', fontWeight: '600' },
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
  },
  header: {
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 30,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 30,
    position: 'relative',
  },
  logo: {
    width: 200,
    height: 100,
  },
  tapIndicator: {
    position: 'absolute',
    top: 0,
    right: '30%',
    backgroundColor: '#FF6B6B',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
  },
  tapIndicatorText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 24,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
    paddingVertical: 5,
  },
  contactLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginLeft: 10,
    flex: 1,
  },
  contactValue: {
    fontSize: 14,
    color: '#333',
    flex: 2,
  },
  linkText: {
    color: '#0066CC',
    textDecorationLine: 'underline',
  },
});
